import React from "react";

import SBCLinkCell from "ComparePlans/Components/SBCLinkCell";
import { useUpdateIndividualApplicationMutation } from "IndividualApplication/api";
import { IndividualApplication, IndividualVisionPlan } from "IndividualApplication/types";
import { enterInformationURL } from "IndividualApplication/urls";
import { useHistory } from "react-router-dom";
import formatCurrency from "Utils/formatCurrency";

import { individualURLHelper, AnalyticsEvent } from "../utils";
import { Body1, SubBody } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { CarrierLogo } from "Cobalt/CarrierLogo";
import { Subtitle } from "Cobalt/Header";
import { Surface, SurfaceSection } from "Cobalt/Surface";
import { Col, Row } from "Common/Grid";
import { Div } from "Common/HTML";

import type { StyleProps } from "Utils/styleProps";

import makeBem from "Utils/makeBem";

const bem = makeBem("IndividualPlanCard");

const detailRow = (name: string, value: string) => (
  <Div key={name} className={bem("visionDetailRow")} mb={3}>
    <SubBody className={bem("visionDetailRow", "detailName")} weight="bold">
      {name}:
    </SubBody>
    <SubBody className={bem("visionDetailRow", "detailValue")}>{value}</SubBody>
  </Div>
);

const formatTerm = (term: string) => {
  if (!term) {
    return "";
  }
  const idx = term.indexOf("<br>") > 0 ? term.indexOf("<br>") : term.indexOf("<br/>");
  return idx > 0 ? term.slice(0, idx) : term;
};

export interface IndividualVisionPlanCardProps extends StyleProps {
  plan: IndividualVisionPlan;
  individualApplication: IndividualApplication;
}

export const IndividualVisionPlanCard = ({ plan, individualApplication }: IndividualVisionPlanCardProps) => {
  const history = useHistory();
  const [updateIndividualApplication] = useUpdateIndividualApplicationMutation();

  const examFrequency = plan.fine_print.Basic["Benefit Frequency Exams"].inNetwork;
  const lensesFrequency = plan.fine_print["Eyeglass Lenses"]["Benefit Frequency Lenses"].inNetwork;
  const framesFrequency = plan?.fine_print.Frames["Benefit Frequency Frames"].inNetwork;
  const contactsFrequency = plan.fine_print["Contact Lenses"]["Benefit Frequency Lenses"].inNetwork;

  const detailsColumn = {
    "Eye Exam": `${formatTerm(plan.eye_exam)} (${examFrequency})`,
    Lenses: `${formatTerm(plan.lenses)} (${lensesFrequency})`,
    Frames: `${formatTerm(plan.frames)} (${framesFrequency})`,
    Contacts: `${formatTerm(plan.contacts)} (${contactsFrequency})`,
  };

  const onSubmit = async (plan) => {
    const submitValues: IndividualApplication = {
      ...individualApplication,
      individualVisionPlanID: plan.id,
    };

    await updateIndividualApplication(submitValues);

    AnalyticsEvent("individual_vision_selected");
    history.push(individualURLHelper(enterInformationURL));
  };

  return (
    <Surface>
      <SurfaceSection>
        <Row>
          <Col xs={3}>
            <CarrierLogo mt={3} plan={plan} />
          </Col>
          <Col xs={7}>
            <Subtitle weight="bold" mt={3}>
              {plan.display_name}
            </Subtitle>
          </Col>
          <Col xs={2} className={bem()}>
            <SubBody mb={2}>Your Cost</SubBody>
            <Body1 weight="bold" className={bem("pricingLabel")}>
              {formatCurrency(plan.total_employee_premium)}
            </Body1>
            <Body1 className={bem("pricingLabel")}>/mo</Body1>
          </Col>
        </Row>
      </SurfaceSection>
      <SurfaceSection>
        <Row>
          <Col xs={6}>{Object.entries(detailsColumn).map(([name, value]) => detailRow(name, value))}</Col>
          <Col xs={1} />
          <Col xs={5}>
            <Div className={bem("SBCRow")} mt={5}>
              <SubBody className={bem("SBCRow", "label")} weight="bold">
                More Details
              </SubBody>
              <SBCLinkCell plan={plan} />
            </Div>
            <Button mt={4} fullWidth onClick={() => onSubmit(plan)}>
              Enroll
            </Button>
          </Col>
        </Row>
      </SurfaceSection>
    </Surface>
  );
};
